import { Checkbox } from '@nextui-org/react'
import { useCallback, useEffect, useState } from 'react'
import { CurieSelect } from '~/components/common/CurieSelect'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import {
  AgentRequest,
  IntegrationPlatform,
  integrationPlatformFromJSON,
  integrationPlatformToJSON
} from '~/protos/agents'
import {
  buildCommandContextUploadFromApiItems,
  type ContextUploadApiItem
} from '~/types/contextUpload'
import { getIntegrationPlatformItemsForKeys } from '~/utils/agentUtils'
import { BaseTaskForm, TaskParams } from './BaseTaskForm'
import { InfoTooltip } from './shared/InfoToolTip'
import {
  SupportingFilesUpload,
  type SupportingFilesUploadState
} from './shared/SupportingFilesUpload'

const REPOSITORY_CODER_SOURCE_PLATFORM_KEYS = [
  'MULESOFT',
  'CAMEL',
  'JAVA_SPRING_BOOT',
  'INFORMATICA',
  'BOOMI',
  'AZURE_INTEGRATION_SERVICES',
  'FLEX_GATEWAY_RUST_POLICY'
]

function normalizeSourcePlatform(platform?: IntegrationPlatform) {
  if (
    !platform ||
    platform === IntegrationPlatform.UNKNOW_INTEGRATION_PLATFORM ||
    platform === IntegrationPlatform.UNRECOGNIZED
  ) {
    return IntegrationPlatform.MULESOFT
  }
  return platform
}

interface CodeEnhancerFormProps {
  onChange: (task: TaskParams) => void
  withExamplePrefilled?: boolean
  initialTaskData?: TaskParams | null
  initVmArgs?: string
  suggestedVmArgs?: string
  isLoadingSuggestedVmArgs?: boolean
  hideVmArgs?: boolean
  disabled?: boolean
  runVerificationDisabled?: boolean
  runVerification?: boolean
  onRunVerificationChange?: (value: boolean) => void
}

export function CodeEnhancerForm({
  onChange,
  withExamplePrefilled,
  initialTaskData,
  initVmArgs = '',
  suggestedVmArgs = '',
  isLoadingSuggestedVmArgs = false,
  hideVmArgs = false,
  disabled = false,
  runVerificationDisabled = false,
  runVerification = false,
  onRunVerificationChange
}: CodeEnhancerFormProps) {
  // Determine initial values - priority: initialTaskData > withExamplePrefilled > defaults
  const getInitialDescription = () => {
    if (initialTaskData?.request?.command?.commandString) {
      return initialTaskData.request.command.commandString
    }
    if (withExamplePrefilled) {
      return `Add debug logs in the process orders flow to log if an order is placed or a canceled order.
Print the value of variables in such cases.`.trim()
    }
    return ''
  }

  const getInitialVmArgs = () => {
    if (initialTaskData?.request?.vmArgs) {
      return initialTaskData.request.vmArgs
    }
    return initVmArgs
  }

  const getInitialSourcePlatform = () => normalizeSourcePlatform(initialTaskData?.request?.platform)

  const getInitialContextUploadData = () => {
    // Supporting files are uploaded directly to S3 and are not restored into the local upload UI.
    return []
  }

  const [description, setDescription] = useState(getInitialDescription())
  const [vmArgs, setVMArgs] = useState(getInitialVmArgs())
  const [sourcePlatform, setSourcePlatform] = useState<IntegrationPlatform>(
    getInitialSourcePlatform()
  )
  const [contextUploadData, setContextUploadData] = useState<ContextUploadApiItem[]>(
    getInitialContextUploadData()
  )
  const [submitBlockedReason, setSubmitBlockedReason] = useState<string | undefined>()

  // Update description when withExamplePrefilled changes
  useEffect(() => {
    // Edge case: Don't update if editing existing task
    if (initialTaskData) return

    // Main case: Update based on withExamplePrefilled
    if (withExamplePrefilled) {
      setDescription(
        `Add debug logs in the process orders flow to log if an order is placed or a canceled order.
Print the value of variables in such cases.`.trim()
      )
    } else {
      setDescription('')
    }
  }, [withExamplePrefilled, initialTaskData])

  // Update VM args when initVmArgs changes (for repository selection changes)
  useEffect(() => {
    // Edge case: Don't update if editing existing task
    if (initialTaskData) return

    // Main case: Update VM args
    setVMArgs(initVmArgs)
  }, [initVmArgs, initialTaskData])

  useEffect(() => {
    if (!initialTaskData) return
    setSourcePlatform(normalizeSourcePlatform(initialTaskData.request?.platform))
  }, [initialTaskData])

  const handleSupportingFilesChange = useCallback(
    ({ contextUploadData, hasPendingUploads, hasFailedUploads }: SupportingFilesUploadState) => {
      setContextUploadData(contextUploadData)
      setSubmitBlockedReason(
        hasPendingUploads
          ? 'Please wait for all supporting files to finish uploading.'
          : hasFailedUploads
            ? 'Some supporting file uploads failed. Please remove them and try again.'
            : undefined
      )
    },
    []
  )

  useEffect(() => {
    const request = AgentRequest.fromPartial({
      command: {
        commandString: description,
        contextUpload: buildCommandContextUploadFromApiItems(contextUploadData)
      },
      platform: sourcePlatform,
      vmArgs
    })
    onChange?.({ request, submitBlockedReason })
  }, [contextUploadData, description, onChange, sourcePlatform, submitBlockedReason, vmArgs])

  return (
    <div className='flex flex-col'>
      <div className='description mb-1'>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] select-none'>
          Source Platform:
        </div>
        <CurieSelect
          placeholder='Select source platform'
          ariaLabel='Source Platform'
          className='w-full sm:w-64 pb-0'
          selectedKeys={[integrationPlatformToJSON(sourcePlatform)]}
          items={getIntegrationPlatformItemsForKeys(REPOSITORY_CODER_SOURCE_PLATFORM_KEYS)}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const key = selectedKeys.values().next().value as string
            setSourcePlatform(integrationPlatformFromJSON(key))
          }}
          disabled={disabled}
        />
      </div>

      <BaseTaskForm
        onChange={({ request }) => setDescription(request.command?.commandString || '')}
        label='Description'
        defaultValue={getInitialDescription()}
        disabled={disabled}
      />

      <div className='mt-4'>
        <SupportingFilesUpload onChange={handleSupportingFilesChange} disabled={disabled} />
      </div>

      <div>
        <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
          <Checkbox
            isSelected={runVerification}
            onChange={e => onRunVerificationChange?.(e.target.checked)}
            isDisabled={runVerificationDisabled}
          >
            <span className='font-semibold text-xs text-default-700 select-none'>
              Run Verification
            </span>
          </Checkbox>
          <InfoTooltip content='Enable thorough verification and validation of the generated code' />
        </div>
        <div className='text-xs text-default-500 ml-6 mb-[10px] select-none'>
          Compiles, deploys, tests, and auto-fixes errors
        </div>
      </div>

      {!hideVmArgs && (
        <div>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center select-none'>
            <div className='flex items-center gap-2'>
              <span>VM Args:</span>
              {isLoadingSuggestedVmArgs && (
                <div className='flex items-center gap-1 text-xs text-gray-500 select-none'>
                  <svg className='animate-spin h-3 w-3' viewBox='0 0 24 24'>
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                      fill='none'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span>Loading VM args suggestions from repository...</span>
                </div>
              )}
            </div>
            <InfoTooltip content='If running the test requires VM arguments, supply them here to ensure the tests execute correctly within the appropriate environment settings' />
          </div>
          <SimpleTextarea
            className='mx-0'
            value={vmArgs}
            onChange={setVMArgs}
            minRows={2}
            placeholder={suggestedVmArgs || ''}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  )
}
